import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useWebSocket } from '../contexts/WebSocketContext';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Users, Calendar, Clock, Star, Sparkles, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

import { LoadingSpinner } from '@/components/ui/LoadingSpinner';


const KnockoutTournamentsClassic = () => {
  const { API, user } = useContext(AuthContext);
  const { lastMessage } = useWebSocket();
  const [tournamentsByMonth, setTournamentsByMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('הכל');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'הכל', label: 'הכל', icon: '', color: 'from-blue-500 to-cyan-500' },
    { id: 'דרג א', label: 'דרג א', icon: '', color: 'from-yellow-500 to-orange-500' },
    { id: 'דרג ב', label: 'דרג ב', icon: '', color: 'from-gray-400 to-gray-500' },
    { id: 'דרג ג', label: 'דרג ג', icon: '', color: 'from-amber-600 to-amber-700' },
    { id: 'יחידים open', label: 'יחידים Open', icon: '', color: 'from-green-500 to-emerald-500' },
    { id: 'זוגות open', label: 'זוגות Open', icon: '', color: 'from-blue-600 to-indigo-500' },
    { id: 'נשים', label: 'נשים', icon: '', color: 'from-pink-500 to-rose-500' },
  ];

  useEffect(() => {
    fetchTournaments();
    // Polling כגיבוי - כל 30 שניות במקום 10
    const interval = setInterval(fetchTournaments, 120000);
    return () => clearInterval(interval);
  }, [selectedCategory]);

  // האזנה לעדכוני טורנירים דרך WebSocket
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'tournament_update') {
      console.log('🎾 Tournament list update received');
      fetchTournaments();
    }
  }, [lastMessage]);

  const fetchTournaments = async () => {
    try {
      const categoryParam = selectedCategory !== 'הכל' ? `?category=${encodeURIComponent(selectedCategory)}` : '';
      const response = await axios.get(`${API}/tournaments/knockout/by-month${categoryParam}`);
      setTournamentsByMonth(response.data);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (tournamentId) => {
    if (!user) {
      toast.error('עליך להיות מחובר כדי להירשם');
      return;
    }

    try {
      const response = await axios.post(`${API}/tournaments/register`, { tournament_id: tournamentId });
      toast.success(response.data.message);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהרשמה');
    }
  };

  const handleUnregister = async (tournamentId) => {
    try {
      const response = await axios.post(`${API}/tournaments/unregister`, { tournament_id: tournamentId });
      toast.success(response.data.message);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בביטול');
    }
  };

  const isUserRegistered = (tournament) => {
    return user && (tournament.registered_players.includes(user.id) || tournament.waiting_list?.includes(user.id));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="relative">
              <Trophy className="w-16 h-16 text-yellow-500" />
              <Star className="w-6 h-6 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-blue-600 via-cyan-500 to-green-500 bg-clip-text text-transparent mb-4" data-testid="knockout-tournaments-title">
            תחרויות נוקאאוט
          </h1>
          <p className="text-lg text-gray-600">
            🎾 תחרויות ארוכות עד 32 שחקנים • מערכת נוקאאוט מלאה
          </p>
        </div>

        {/* Search */}
        <div className="mb-8 max-w-md mx-auto">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="🔍 חפש תחרות..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-12 py-6 text-lg rounded-2xl border-2 border-gray-200 focus:border-blue-500 shadow-lg"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Category Tabs - New Impressive Design */}
        <div className="mb-10" data-testid="category-tabs">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`category-tab-${category.id}`}
                className={`
                  px-5 py-3 rounded-xl font-bold text-sm transition-all duration-300 transform
                  flex items-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-1
                  ${selectedCategory === category.id
                    ? `bg-gradient-to-r ${category.color} text-white scale-105`
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                  }
                `}
              >
                {category.icon && <span className="text-lg">{category.icon}</span>}
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white/60 rounded-xl p-6 animate-pulse" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center">
              <LoadingSpinner size="lg" text="טוען תחרויות נוקאאוט..." />
            </div>
          </div>
        ) : tournamentsByMonth.length > 0 ? (
          <div className="space-y-12">
            {tournamentsByMonth.map((monthData, monthIdx) => (
              <div key={monthIdx} className="space-y-6 animate-page-in" style={{ animationDelay: `${monthIdx * 0.1}s` }}>
                {/* כותרת חודש */}
                <div className="flex items-center gap-3 border-b-2 border-blue-500 pb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">{monthData.month_display}</h2>
                </div>
                
                {/* טורנירים בחודש */}
                {monthData.tournaments && monthData.tournaments.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {monthData.tournaments.map((tournament, tIdx) => {
                      // קביעת סטטוס וצבע
                      const getStatusInfo = () => {
                        if (tournament.status === 'upcoming') {
                          return { label: '🔒 יפתח בקרוב', color: 'bg-blue-600 text-white', border: 'border-blue-400', headerBg: 'bg-gradient-to-r from-blue-600 to-blue-700' };
                        }
                        if (tournament.status === 'in_progress') {
                          return { label: '🔴 LIVE - בתהליך', color: 'bg-red-500 text-white animate-pulse', border: 'border-red-400', headerBg: 'bg-gradient-to-r from-red-500 to-orange-500' };
                        }
                        if (tournament.status === 'completed') {
                          return { label: '✅ הסתיים', color: 'bg-blue-500 text-white', border: 'border-blue-400', headerBg: 'bg-gradient-to-r from-blue-500 to-blue-600' };
                        }
                        if (tournament.status === 'full') {
                          return { label: '🔒 מלא', color: 'bg-orange-500 text-white', border: 'border-orange-400', headerBg: 'bg-gradient-to-r from-orange-500 to-yellow-500' };
                        }
                        return { label: '✨ פתוח להרשמה', color: 'bg-green-500 text-white', border: 'border-green-400', headerBg: 'bg-gradient-to-r from-blue-500 to-cyan-500' };
                      };
                      const statusInfo = getStatusInfo();
                      
                      // בדיקה אם המשתמש הוא אדמין
                      const isAdmin = user?.is_admin || ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email);
                      // האם ההרשמה פתוחה - רק אם לא התחיל או שהמשתמש אדמין
                      const canRegister = tournament.status === 'open' || (tournament.status === 'in_progress' && isAdmin);
                      
                      return (
                        <div 
                          key={tournament.id} 
                          className="list-item-animate"
                          style={{ animationDelay: `${tIdx * 0.05}s` }}
                        >
                          <Card className={`h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 ${statusInfo.border} overflow-hidden`} data-testid={`tournament-card-${tournament.id}`}>
                            {/* Header with gradient */}
                            <Link to={`/tournament/${tournament.id}`}>
                              <div className={`p-4 ${statusInfo.headerBg} cursor-pointer`}>
                                <div className="flex justify-between items-start">
                                  <h3 className="text-xl font-bold text-white truncate flex-1">{tournament.name}</h3>
                                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusInfo.color} shadow-md whitespace-nowrap mr-2`}>
                                    {statusInfo.label}
                                  </span>
                                </div>
                                <p className="text-white/90 text-sm mt-1">{tournament.category} • נוקאאוט</p>
                              </div>
                            </Link>
                            
                            <CardContent className="p-4 space-y-3">
                              {/* תאריך */}
                              <div className="flex items-center gap-3 text-gray-700">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                  <Calendar className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">תאריך</p>
                                  <p className="font-semibold">{new Date(tournament.date).toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                                </div>
                              </div>
                              
                              {/* שעה ומיקום */}
                              <div className="flex items-center justify-between">
                                {tournament.start_time && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-2xl">🕐</span>
                                    <span className="font-bold text-blue-600">{tournament.start_time}</span>
                                  </div>
                                )}
                                {tournament.location && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xl">📍</span>
                                    <span className="font-medium text-green-600 text-sm">{tournament.location}</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* שחקנים */}
                              <div className="flex items-center justify-between pt-2 border-t">
                                <div className="flex items-center gap-2">
                                  <Users className="w-5 h-5 text-blue-500" />
                                  <span className="text-gray-600">{tournament.category?.includes('זוגות') ? 'זוגות:' : 'משתתפים:'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xl font-bold text-blue-600">
                                    {tournament.category?.includes('זוגות')
                                      ? (tournament.registered_players_names?.length || Math.ceil((tournament.registered_players?.length || 0) / 2))
                                      : (tournament.registered_players?.length || 0)}
                                  </span>
                                  <span className="text-gray-400">/</span>
                                  <span className="text-gray-500">
                                    {tournament.max_participants || tournament.max_players || 32}
                                  </span>
                                </div>
                              </div>
                              
                              {/* שמות משתתפים */}
                              {tournament.registered_players_names && tournament.registered_players_names.length > 0 && (
                                <div className="max-h-20 overflow-y-auto bg-gray-50 rounded-lg p-2">
                                  <div className="flex flex-wrap gap-1">
                                    {tournament.registered_players_names.map((name, idx) => (
                                      <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* רשימת המתנה */}
                              {tournament.waiting_list && tournament.waiting_list.length > 0 && (
                                <div data-testid={`waiting-list-names-${tournament.id}`} className="bg-orange-50 border border-orange-200 p-2 rounded-lg">
                                  <div className="flex items-center justify-between text-orange-600 mb-1">
                                    <span className="text-sm font-medium">רשימת המתנה:</span>
                                    <span className="font-bold text-sm">{tournament.waiting_list.length}</span>
                                  </div>
                                  {tournament.waiting_list_names && tournament.waiting_list_names.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {tournament.waiting_list_names.map((name, idx) => (
                                        <span key={idx} className="text-xs text-orange-500">{name}{idx < tournament.waiting_list_names.length - 1 ? ',' : ''}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* הודעת רשימת המתנה לתחרויות מלאות */}
                              {tournament.status === 'full' && (
                                <div data-testid={`waiting-list-msg-${tournament.id}`} className="text-center py-2 px-3 bg-amber-50 border border-amber-300 rounded-lg">
                                  <p className="text-amber-800 text-sm font-bold" dir="rtl">
                                    📋 ניתן עדיין להירשם לרשימת ההמתנה!
                                  </p>
                                </div>
                              )}
                              
                              {/* הודעה על סגירת הרשמה */}
                              {tournament.status === 'in_progress' && !isUserRegistered(tournament) && (
                                <div data-testid={`live-closed-${tournament.id}`} className="flex items-center justify-center text-red-600 bg-red-50 p-2 rounded-lg border border-red-200">
                                  <span className="text-sm font-bold">התחרות החלה - ההרשמה סגורה</span>
                                </div>
                              )}
                              
                              {/* כפתורים */}
                              <div className="flex gap-2 pt-2">
                                {tournament.category?.includes('זוגות') ? (
                                  /* תחרות זוגות - כפתור אחד גדול שמוביל לדף התחרות */
                                  <>
                                    {user && tournament.status !== 'completed' && isUserRegistered(tournament) && (
                                      <Button 
                                        variant="destructive" 
                                        className="flex-1"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleUnregister(tournament.id);
                                        }}
                                        data-testid={`unregister-btn-${tournament.id}`}
                                      >
                                        הסר אותי
                                      </Button>
                                    )}
                                    <Link to={`/tournament/${tournament.id}`} className="flex-1">
                                      <Button data-testid={`doubles-details-btn-${tournament.id}`} className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold text-base py-3">
                                        {tournament.status === 'in_progress' ? 'צפה בפרטים' : 'צפה בפרטים והרשמה'} &larr;
                                      </Button>
                                    </Link>
                                  </>
                                ) : (
                                  /* תחרות יחידים - כפתורי הרשמה + צפה בפרטים */
                                  <>
                                    {user && tournament.status !== 'completed' && tournament.status !== 'in_progress' && canRegister && (
                                      isUserRegistered(tournament) ? (
                                        <Button 
                                          variant="destructive" 
                                          className="flex-1"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleUnregister(tournament.id);
                                          }}
                                          data-testid={`unregister-btn-${tournament.id}`}
                                        >
                                          הסר אותי
                                        </Button>
                                      ) : (
                                        <Button 
                                          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleRegister(tournament.id);
                                          }}
                                          data-testid={`register-btn-${tournament.id}`}
                                        >
                                          הירשם עכשיו
                                        </Button>
                                      )
                                    )}
                                    <Link to={`/tournament/${tournament.id}`} className="flex-1">
                                      <Button variant="outline" className="w-full border-2 border-blue-500 text-blue-600 hover:bg-blue-50">
                                        צפה בפרטים &larr;
                                      </Button>
                                    </Link>
                                  </>
                                )}
                              </div>
                              
                              {/* כפתור תשלום */}
                              {tournament.status !== 'completed' && (
                                <a 
                                  href="https://links.payboxapp.com/5tsaxw82LUb"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow-lg">
                                    💳 לתשלום דרך Paybox
                                  </Button>
                                </a>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    אין תחרויות בחודש זה
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">אין תחרויות בקטגוריה זו</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnockoutTournamentsClassic;
